import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { AuthenticationService } from '../services/authentication.service';
import { TrackingService } from '../services/tracking.service';

export const CanActivateAuth: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthenticationService);
  const trackingService = inject(TrackingService);
  const router = inject(Router);

  return AuthGuard.canActivate(route, state, authService, trackingService, router);
};

export class AuthGuard {
  private static authQueryMappings = [
    {
      input: 'src',
      output: 'referrerCode',
      // Match anything except empt
      pattern: /^(.+)$/,
      replacement: 'src_$1',
      strip: false,
    },
    {
      input: 'cid',
      output: 'referrerCode',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
    {
      input: 'sig',
      output: 'sig',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
    {
      input: 'acr_values',
      output: 'acr_values',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
  ];

  static async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authService: AuthenticationService,
    trackingService: TrackingService,
    router: Router,
  ) {
    // Use the synchronous id-token check (not isAuthenticatedAnywhere, which can trigger a
    // silent-refresh that redirects to the Duende login). During the CIAM migration we must not
    // force a legacy login; see the fallthrough below.
    if (authService.isAuthenticated()) {
      const url = location.href.split(location.host)[1];
      const matchSrc = url && /[&?]src=([^&#]+)/.exec(url);
      if (matchSrc) {
        trackingService.trackMarketingSource(matchSrc[1]);
        if (route.queryParams.src) {
          await router.navigateByUrl(AuthGuard.stripParameterFromUrl(url, 'src'), {
            replaceUrl: true,
          });
        }
      }

      return true;
    }

    const queryState = route.queryParams.state;
    if (queryState) {
      const stateOrigin = queryState.split(/\b\/\b/)[0];

      if (stateOrigin.toLowerCase() !== location.origin.toLowerCase()) {
        throw new Error(`Unexpected origin: ${stateOrigin}`);
      }

      setTimeout(() => (location.href = queryState));
      return false;
    }

    // CIAM migration: do NOT force an interactive login against the self-hosted Duende identity
    // server (being decommissioned). Authentication is enforced server-side by the API — a FinQuery
    // Keycloak bearer token or the LeaseQuery "APIAuth" cookie — and 401s are surfaced by the HTTP
    // interceptors. Allow route activation instead of redirecting to Duende. When the SPA adopts the
    // CIAM auth-code + PKCE flow, this guard should validate the Keycloak session here instead.
    return true;
  }

  static stripParameterFromUrl(url: string, parameter: string) {
    return url.replace(new RegExp(`([&?])${parameter}=[^&#]*&?`), '$1').replace(/[?&]$/, '');
  }

  static getReturnUrlAndCustomQueryParams(url: string) {
    let returnUrl = url;

    const queryParams = Object.fromEntries(
      (url.split(/\?/)[1] ?? '').split(/&/g).map(x => x.split(/=/)),
    );

    const customQueryParams: { [key: string]: string } = {};

    for (const mapping of AuthGuard.authQueryMappings) {
      const value = queryParams[mapping.input];
      if (value && mapping.pattern.test(value)) {
        customQueryParams[mapping.output] = mapping.replacement
          ? value.replace(mapping.pattern, mapping.replacement)
          : value;
        if (mapping.strip) {
          returnUrl = AuthGuard.stripParameterFromUrl(returnUrl, mapping.input);
        }
      }
    }

    return { returnUrl, customQueryParams };
  }
}
